import React from 'react';
import { Table, Spinner, Badge } from 'react-bootstrap';
import { useQuery } from 'react-query';
import * as moment from 'moment';
import { Get } from '@app/utils/httpProvider';
import { LICHSU_THAYDOI_HOSO_API } from '@app/utils/apiUrl';
import { objectLoaiYeuCau, loaiYeuCauValue } from '@app/utils/define';

const FIELD_LABELS = {
    TenDeTai: 'Tên đề tài',
    DenNgay: 'Thời hạn đào tạo đến ngày',
    SoThangGiaHan: 'Số tháng gia hạn',
    HoTen: 'Họ tên',
    DonViCongTac: 'Đơn vị công tác',
    LoaiCanBoHuongDan: 'Loại cán bộ hướng dẫn'
};

const getFieldLabel = (key) => FIELD_LABELS[key] || key;

const LOAI_CBHD_LABELS = {
    canBoHuongDanChinh: 'Hướng dẫn chính',
    canBoHuongDanPhu: 'Hướng dẫn phụ',
    dongHuongDan: 'Đồng hướng dẫn'
};

const getLoaiCanBoLabel = (val) => LOAI_CBHD_LABELS[val] || val;

const formatValue = (key, val) => {
    if (val === null || val === undefined) return 'Trống';
    if (key === 'DenNgay' && val) return moment(val).format('DD/MM/YYYY');
    if (key === 'LoaiCanBoHuongDan') return getLoaiCanBoLabel(val);
    return String(val);
};

const renderCanBoDaoTao = (list) => {
    if (!list || list.length === 0) return <em>Không có</em>;
    return (
        <div>
            {list.map((cb, idx) => (
                <div key={idx} className="mb-2 p-2 border rounded bg-white">
                    <div><strong>{idx + 1}. {cb.HoTen}</strong></div>
                    <div className="text-muted small">
                        {cb.DonViCongTac && <span>Đơn vị: {cb.DonViCongTac}</span>}
                        {cb.LoaiCanBoHuongDan && <span className="ml-3">Vai trò cán bộ: {getLoaiCanBoLabel(cb.LoaiCanBoHuongDan)}</span>}
                    </div>
                </div>
            ))}
        </div>
    );
};

const renderJsonData = (jsonStr, loaiThayDoi) => {
    if (!jsonStr) return <em>Không có</em>;
    try {
        const parsed = JSON.parse(jsonStr);

        if (loaiThayDoi === loaiYeuCauValue.thayDoiCBHD && parsed.CanBoDaoTao) {
            return renderCanBoDaoTao(parsed.CanBoDaoTao);
        }

        return (
            <ul className="mb-0 pl-3 list-unstyled">
                {Object.keys(parsed).map((key, idx) => (
                    <li key={idx} className="mb-1">
                        <strong>{getFieldLabel(key)}:</strong>{' '}
                        <span>{formatValue(key, parsed[key])}</span>
                    </li>
                ))}
            </ul>
        );
    } catch (e) {
        return <span>{jsonStr}</span>;
    }
};

const getLoaiThayDoiLabel = (val) => {
    const found = objectLoaiYeuCau.find(x => x.value === val);
    return found ? found.label : val;
};

const getLoaiBadgeVariant = (val) => {
    switch (val) {
        case loaiYeuCauValue.thayDoiDeTai: return 'info';
        case loaiYeuCauValue.thayDoiCBHD: return 'warning';
        case loaiYeuCauValue.giaHanDaoTao: return 'success';
        default: return 'secondary';
    }
};

const LichSuThayDoiHoSoNCSTab = ({ hoSoId }) => {
    const { data: lichSuRes, isLoading } = useQuery(
        ['lichSuThayDoi', hoSoId], 
        () => Get(`${LICHSU_THAYDOI_HOSO_API.GET_BY_HOSO_ID}/${hoSoId}`), 
        { enabled: !!hoSoId }
    );
    const items = lichSuRes?.data?.$values || lichSuRes?.data || lichSuRes || [];
    const listData = Array.isArray(items) ? items : [];

    if (isLoading) {
        return (
            <div className="text-center py-5">
                <Spinner animation="border" variant="primary" />
                <p className="mt-2 text-muted">Đang tải dữ liệu lịch sử...</p>
            </div>
        );
    }

    if (listData.length === 0) {
        return <div className="alert alert-secondary mt-3">Không có lịch sử thay đổi.</div>;
    }

    return (
        <div className="bg-light p-3 rounded border mt-3">
            <h5 className="font-weight-bold mb-3 border-bottom pb-2" style={{ color: "#1e7e34" }}>
                <i className="fas fa-history mr-2"></i> Lịch sử thay đổi hồ sơ ({listData.length} lần thay đổi)
            </h5>
            
            <div className="timeline-container mt-4">
                {listData.map((item, index) => (
                    <div key={item.id || index} className="card mb-3 shadow-sm" style={{ borderLeft: '4px solid #007bff' }}>
                        <div className="card-header bg-white d-flex justify-content-between align-items-center py-2">
                            <div>
                                <Badge variant={getLoaiBadgeVariant(item.loaiThayDoi)} className="mr-2 p-2" style={{ fontSize: '13px' }}>
                                    {getLoaiThayDoiLabel(item.loaiThayDoi)}
                                </Badge>
                                <strong>Ngày duyệt:</strong>{' '}
                                <span className="text-primary">
                                    {item.ngayThayDoi ? moment(item.ngayThayDoi).format('DD/MM/YYYY HH:mm') : ''}
                                </span>
                            </div>
                            <small className="text-muted">Lần thay đổi {listData.length - index} — Người duyệt: {item.createdBy}</small>
                        </div>
                        <div className="card-body p-0">
                            <Table bordered hover size="sm" className="mb-0">
                                <thead className="bg-light">
                                    <tr>
                                        <th style={{ width: '50%' }} className="text-success">
                                            <i className="fas fa-arrow-right mr-1"></i>Thông tin sau khi thay đổi (Hiện tại)
                                        </th>
                                        <th style={{ width: '50%' }} className="text-danger">
                                            <i className="fas fa-arrow-left mr-1"></i>Thông tin trước khi thay đổi (Cũ)
                                        </th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr>
                                        <td className="align-top p-3" style={{ wordBreak: 'break-word', backgroundColor: '#f0fff4' }}>
                                            {renderJsonData(item.duLieuMoi, item.loaiThayDoi)}
                                        </td>
                                        <td className="align-top p-3" style={{ wordBreak: 'break-word', backgroundColor: '#fff5f5' }}>
                                            {renderJsonData(item.duLieuCu, item.loaiThayDoi)}
                                        </td>
                                    </tr>
                                </tbody>
                            </Table>
                        </div>
                    </div>
                ))}
            </div>
        </div>
    );
};

export default LichSuThayDoiHoSoNCSTab;
